// src/admin/components/RoleFormModal.jsx
//
// Create/edit a role. Replaces the always-open inline form at the top of the
// old RolesPage — name, level, description, and the permission checkbox list
// — now behind the header's action button, same as
// accounts/components/RegisterAccountModal.jsx and
// Connections/components/RegisterConnectionModal.jsx.
//
// size="lg" (960px, and the default 88vh tall). Not "xl": that's
// min(1700px, 97vw) AND capped at 75vh — the widest and shortest variant,
// which stretched the permission rows into mostly whitespace while giving the
// list less vertical room. The picker scrolls internally rather than the whole
// dialog, so the footer buttons stay reachable.
//
// One component serves both modes: `role` null means create, otherwise edit.

import { useState } from "react";
import PropTypes from "prop-types";
import { CheckCircle2, XCircle } from "lucide-react";
import { Modal, FormField, Input, Select, Button, Alert, SkeletonText, useDiscardConfirm } from "../../shared/ui";
import { ROLE_LEVELS } from "../api/rolesApi";
import PermissionPicker from "./PermissionPicker";

export default function RoleFormModal({
  open,
  role,
  permissions,
  permissionsLoading,
  permissionsError,
  onClose,
  onSubmit,
}) {
  const isEdit = Boolean(role);

  const [values, setValues] = useState({
    name: role?.name || "",
    level: role?.level || "org",
    description: role?.description || "",
    permissionNames: role?.permissions || [],
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [saved, setSaved] = useState(null);
  const [dirty, setDirty] = useState(false);

  function handleClose() {
    setErrors({});
    setSaved(null);
    setSubmitting(false);
    setDirty(false);
    onClose();
  }

  const { requestClose, confirmDialog } = useDiscardConfirm(dirty && !saved, handleClose);

  function setField(name, v) {
    setValues((prev) => ({ ...prev, [name]: v }));
    setDirty(true);
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    const nextErrors = {};
    if (!values.name.trim()) nextErrors.name = "Role name is required.";
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    setSubmitting(true);
    try {
      const result = await onSubmit({
        name: values.name.trim(),
        level: values.level,
        description: values.description.trim(),
        permissionNames: values.permissionNames,
      });
      setSaved(result);
    } catch (err) {
      // A 409/422 arrives with fieldErrors from toAdminError (admin/api/
      // adminApiError.js) so "already exists" lands under the name input;
      // anything else is a banner.
      if (err?.fieldErrors) setErrors(err.fieldErrors);
      else setErrors({ _form: err?.message || "Save failed. Please try again." });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <Modal
        open={open}
        size="lg"
        title={isEdit ? `Edit role — ${role.name}` : "New role"}
        onClose={requestClose}
      >
        {saved ? (
          <div className="flex flex-col gap-4">
            <Alert
              variant="success"
              icon={CheckCircle2}
              title={isEdit ? `"${saved.name}" updated` : `"${saved.name}" created`}
            >
              {saved.permissions.length === 0
                ? "It grants nothing yet — edit it to attach permissions."
                : `It grants ${saved.permissions.length} permission${saved.permissions.length === 1 ? "" : "s"}. Anyone already holding it picks this up the next time they sign in.`}
            </Alert>
            <div className="flex justify-end">
              <Button variant="primary" onClick={handleClose}>Done</Button>
            </div>
          </div>
        ) : (
          <form className="flex flex-col gap-4" onSubmit={handleSubmit}>
            {errors._form && (
              <Alert variant="danger" icon={XCircle} title="Couldn't save">
                {errors._form}
              </Alert>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField label="Name" required error={Boolean(errors.name)} errorText={errors.name}>
                <Input
                  value={values.name}
                  onChange={(e) => setField("name", e.target.value)}
                  placeholder="e.g. sow_org_admin"
                  autoFocus
                />
              </FormField>

              <FormField
                label="Level"
                required
                helperText={
                  isEdit
                    ? "Fixed once created — each level attaches through a different table."
                    : "Where this role can be granted. Can't be changed later."
                }
              >
                <Select
                  options={ROLE_LEVELS}
                  value={values.level}
                  onChange={(e) => setField("level", e.target.value)}
                  disabled={isEdit}
                />
              </FormField>
            </div>

            <FormField label="Description" helperText="What someone holding this role is expected to do.">
              <Input
                value={values.description}
                onChange={(e) => setField("description", e.target.value)}
                placeholder="e.g. Manages SOW analysis for the whole organization"
              />
            </FormField>

            <FormField
              label="Permissions"
              helperText="Saving replaces the role's permissions with exactly this selection."
            >
              {permissionsLoading ? (
                <div className="p-3 border border-line rounded-card">
                  <SkeletonText lines={5} />
                </div>
              ) : permissionsError ? (
                <Alert variant="danger" icon={XCircle} title="Couldn't load the permission catalog">
                  {permissionsError} Close this dialog and retry — saving now would
                  clear the role&apos;s existing permissions.
                </Alert>
              ) : (
                <PermissionPicker
                  permissions={permissions}
                  value={values.permissionNames}
                  onChange={(next) => setField("permissionNames", next)}
                />
              )}
            </FormField>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={handleClose}>Cancel</Button>
              <Button
                type="submit"
                variant="primary"
                loading={submitting}
                // Submitting while the catalog failed to load would send an
                // empty permission_names, and PUT replaces rather than merges —
                // silently stripping every permission off the role.
                disabled={permissionsLoading || Boolean(permissionsError)}
              >
                {isEdit ? "Save changes" : "Create role"}
              </Button>
            </div>
          </form>
        )}
      </Modal>
      {confirmDialog}
    </>
  );
}

RoleFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  /** null → create mode; a record → edit mode, seeded from it. */
  role: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    level: PropTypes.string,
    description: PropTypes.string,
    permissions: PropTypes.arrayOf(PropTypes.string),
  }),
  permissions: PropTypes.array.isRequired,
  permissionsLoading: PropTypes.bool,
  permissionsError: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
